/**
 * Central BYOD-app session helpers (DATA-001 / DATA-002).
 * All reads are try/catch-safe; writers merge through one path to avoid
 * clobbering concurrent fields via deferred read-modify-write.
 */

export const BYOD_APP_STORAGE_KEY = 'BYOD-app';

export interface ByodAppSessionUser {
	id?: number | string;
	'@id'?: string;
	email?: string;
	sharedEmployeeWorkplace?: string | null;
	userMustChangePasswordOnNextLogin?: boolean;
	[key: string]: unknown;
}

export type ByodAppWorkplaceRef = number | string | { id?: number | string } | null;

export interface ByodAppSession {
	token_key?: string;
	expiresAt?: string | number;
	usedBy?: unknown;
	user?: ByodAppSessionUser | null;
	role?: unknown;
	workplace?: ByodAppWorkplaceRef;
	timezone?: string;
	timeZones?: string;
	[key: string]: unknown;
}

export function getByodAppSession(): ByodAppSession | null {
	try {
		const raw = localStorage.getItem(BYOD_APP_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		return parsed as ByodAppSession;
	} catch {
		return null;
	}
}

export function setByodAppSession(session: ByodAppSession): void {
	localStorage.setItem(BYOD_APP_STORAGE_KEY, JSON.stringify(session));
}

/** Merge patch into current session (or empty object) and persist atomically. */
export function updateByodAppSession(patch: Partial<ByodAppSession>): ByodAppSession {
	const current = getByodAppSession() ?? {};
	const next: ByodAppSession = { ...current, ...patch };
	setByodAppSession(next);
	return next;
}

/** Resolve workplace id whether stored as a scalar or `{ id }`. */
export function resolveWorkplaceId(
	workplace: ByodAppWorkplaceRef | undefined,
): number | string | null {
	if (workplace == null) return null;
	if (typeof workplace === 'object') {
		return workplace.id ?? null;
	}
	return workplace;
}
